import React, {useContext} from 'react';
import {classes, style} from './doppe-action-type-widget-content-layout-new-sections-and-buttons.st.css';
import {
    DoppeActionTypeWidgetContentSection
} from '../doppe-action-type-widget-content-section/doppe-action-type-widget-content-section';
import {uiPartCreateMarking, Values} from '@wix/devzai-utils-common';
import {DoppeViewerScrollView} from '../doppe-viewer-scroll-view/doppe-viewer-scroll-view';
import {FlexLayout} from '@wix/devzai-common-client';
import {ScrollViewScrollingDirection} from '@wix/devzai-utils-react';
import {DoppeActionTypeWidgetLayoutContext} from '../doppe-action-type-widget-layout/doppe-action-type-widget-layout';
import {DoppeActionContentSection} from '../../client-server-common/types/doppe-action-content-section';
import {
    DoppeActionTypeWidgetContentSections
} from '../doppe-action-type-widget-content-sections/doppe-action-type-widget-content-sections';
import {DoppeActionCTAButton} from '../../client-server-common/types/doppe-action-cta-button';
import {
    DoppeActionTypeWidgetContentLayoutNewSectionsAndButtonsParts
} from './doppe-action-type-widget-content-layout-new-sections-and-buttons.ui-parts';
import {
    DoppeActionWidgetCtaButtonsList
} from '../doppe-action-widget-cta-buttons-list/doppe-action-widget-cta-buttons-list';
import {
    doppeActionCtaButtonsSettingsGetVisibleButtonsCount
} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import {DoppeActionGoalType} from '../../client-server-common/doppe-action-types/doppe-action-type';
import {
    doppeActionContentSectionsSettingsGetVisibleButtonsCount
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import {IDoppeViewerActionContext} from '../../doppe-sdk';

export const DoppeActionTypeWidgetContentLayoutNewSectionsAndButtonsContentAlignment = {
    Default: 'default',
    Center: 'center'
} as const;

export type DoppeActionTypeWidgetContentLayoutNewSectionsAndButtonsContentAlignment = Values<typeof DoppeActionTypeWidgetContentLayoutNewSectionsAndButtonsContentAlignment>;


export const DoppeActionTypeWidgetContentLayoutNewSectionsAndButtons = React.memo(function DoppeActionTypeWidgetContentLayoutNewSectionsAndButtons(props: DoppeActionTypeWidgetContentLayoutNewSectionsAndButtons.Props) {
    const {
        children,
        sections,
        buttons,
        buttonsSettings,
        mainMedia,
        fullWidthMainMedia = false,
        contentAlignment = DoppeActionTypeWidgetContentLayoutNewSectionsAndButtonsContentAlignment.Default,
        isFixedHeight = false,
        noContentPadding = false,
        noContentTopPadding = false,
        noContentSectionsPadding = false,
        maxButtonsLimitForFixedFooter = 2,
        actionContext,
        reportGoalAchieved,

        className,
        ...htmlAttributes
    } = props;


    const doppeActionTypeWidgetLayoutContext = useContext(DoppeActionTypeWidgetLayoutContext)

    const forceMaxHeight = doppeActionTypeWidgetLayoutContext?.forceMaxHeight ?? false;
    const headerSpec = doppeActionTypeWidgetLayoutContext?.headerSpec ?? null;
    const isStrip = doppeActionTypeWidgetLayoutContext?.isStrip ?? false;

    const shouldRenderMainMediaInFullWidth = isStrip ? false : fullWidthMainMedia;

    const visibleButtonsCount = doppeActionCtaButtonsSettingsGetVisibleButtonsCount(buttons);
    const visibleSectionsCount = doppeActionContentSectionsSettingsGetVisibleButtonsCount(sections);

    const hasButtons = visibleButtonsCount > 0;
    const hasSections = visibleSectionsCount > 0;

    const content = (
        <>
            {mainMedia ? (
                shouldRenderMainMediaInFullWidth ?
                    <div
                        className={style(classes.mainMediaContainer, {})}
                    >
                        {mainMedia}
                    </div>
                    :
                    <DoppeActionTypeWidgetContentSection
                        className={style(classes.mediaContentSection, {})}
                    >
                        <div
                            className={style(classes.mainMediaContainer, {})}
                        >
                            {mainMedia}
                        </div>
                    </DoppeActionTypeWidgetContentSection>
            ) : null}
            {
                children ?
                    <DoppeActionTypeWidgetContentSection
                        noPadding={noContentPadding}
                        className={style(classes.content)}
                        {...uiPartCreateMarking(DoppeActionTypeWidgetContentLayoutNewSectionsAndButtonsParts.Content)}
                    >
                        {children}
                    </DoppeActionTypeWidgetContentSection>
                    :
                    null
            }
            {
                hasSections ?
                    <DoppeActionTypeWidgetContentSection
                        noPadding={noContentSectionsPadding}
                        className={style(classes.sectionsContent)}
                        {...uiPartCreateMarking(DoppeActionTypeWidgetContentLayoutNewSectionsAndButtonsParts.Sections)}
                    >
                        <DoppeActionTypeWidgetContentSections
                            withInnerPadding={noContentSectionsPadding}
                            contentSections={sections}
                            className={style(classes.sections)}
                            rootActionContext={actionContext}
                            hasButtons={hasButtons}
                        />
                    </DoppeActionTypeWidgetContentSection>
                    :
                    null
            }
        </>
    );

    const buttonsSection = hasButtons ? (
        <DoppeActionTypeWidgetContentSection
            className={style(classes.footerSection, {})}
            {...uiPartCreateMarking(DoppeActionTypeWidgetContentLayoutNewSectionsAndButtonsParts.Buttons)}
        >
            <div
                className={style(classes.footerContent, {})}
            >
                <DoppeActionWidgetCtaButtonsList
                    actionContext={actionContext}
                    ctaButtonsSettings={{
                        ctaButtons: buttons
                    }}
                    onClick={(goalType) => {
                        reportGoalAchieved(goalType);
                    }}
                    {...buttonsSettings}
                />
            </div>
        </DoppeActionTypeWidgetContentSection>
    ) : null


    return (
        <div
            {...htmlAttributes}
            className={style(
                classes.root,
                {
                    hasContent: !!children,
                    hasSections: hasSections,
                    hasButtons: hasButtons,
                    hasMainMedia: !!mainMedia,
                    contentAlignment: contentAlignment,
                    fullWidthMainMedia: shouldRenderMainMediaInFullWidth,
                    isFixedHeight: isFixedHeight,
                    noContentPadding: noContentPadding,
                    noContentTopPadding: noContentTopPadding,
                    noContentSectionsPadding: noContentSectionsPadding,
                    forceMaxHeight: forceMaxHeight,
                    hasHeader: headerSpec !== null,
                    hasHeaderWithBottomBorder: headerSpec?.hasBorderBottom ?? false
                },
                className
            )}
        >
            {
                isFixedHeight ?
                    <DoppeViewerScrollView
                        className={style(classes.scrollView, {}, FlexLayout.fillRemainingVerticalSpace)}
                        scrolledContentWrapperClassName={style(classes.innerScrollContent, {}, FlexLayout.column.default)}
                        scrollingDirection={ScrollViewScrollingDirection.Vertical}
                    >
                        {content}
                        {visibleButtonsCount > maxButtonsLimitForFixedFooter ? buttonsSection : null}
                    </DoppeViewerScrollView>
                    :
                    content
            }
            {visibleButtonsCount <= maxButtonsLimitForFixedFooter || !isFixedHeight ? buttonsSection : null}
        </div>
    );
});

export namespace DoppeActionTypeWidgetContentLayoutNewSectionsAndButtons {

    export interface Props extends React.HTMLAttributes<any> {
        sections: DoppeActionContentSection[];
        buttons: DoppeActionCTAButton.MultipleButton[]
        buttonsSettings?: Pick<DoppeActionWidgetCtaButtonsList.Props, 'ctaCallback' | 'listItemId' | 'sourceURL'>,
        mainMedia?: React.ReactNode;
        fullWidthMainMedia?: boolean;
        isFixedHeight?: boolean;
        contentAlignment?: DoppeActionTypeWidgetContentLayoutNewSectionsAndButtonsContentAlignment;
        noContentPadding?: boolean;
        noContentTopPadding?: boolean;
        noContentSectionsPadding?: boolean;
        maxButtonsLimitForFixedFooter?: number;
        actionContext:  IDoppeViewerActionContext<any>;
        reportGoalAchieved: (goalType: DoppeActionGoalType) => void;
    }
}
